import { MedplumClient } from "@medplum/core";
import { getPatientName } from "../Eligibility/EligibiltyService";
import { addInputToJson, parseAndCategorize } from "../AIRecipes/aiService";
import { showNotification } from "@mantine/notifications";

export function updateSummaryWithRelevantFields(
  summaryBody: any,
  patient: any,
  conditions: any,
  observations: any,
  encounter: any
) {
  const age = patient?.birthDate
    ? Math.floor(
        (new Date().getTime() - new Date(patient.birthDate).getTime()) /
          (1000 * 60 * 60 * 24 * 365.25)
      )
    : null;

  // Update patient info
  summaryBody[0].patient_info = {
    name: getPatientName(patient?.name),
    age: age,
    gender: patient?.gender,
    ethnicity: "Caucasian",
  };

  // Update medical history based on conditions
  summaryBody[0].medical_history = conditions.map((condition: any) => ({
    condition: condition.resource.code.text,
    diagnosis_date: condition.resource.onsetDateTime.split("T")[0],
  }));

  // Update lab results and vital signs based on observations
  summaryBody[0].lab_results = [];
  summaryBody[0].vital_signs = [];

  observations.forEach((observation: any) => {
    const code = observation.resource.code.coding[0].code;
    const value = observation.resource.valueQuantity.value;

    if (code === "4548-4") {
      // Hemoglobin A1c
      summaryBody[0].lab_results.push({
        date: observation?.resource?.effectiveDateTime?.split("T")[0],
        test: "Hemoglobin A1c",
        result: `${value} ${observation?.resource?.valueQuantity?.unit}`,
      });
    } else if (code === "29463-7") {
      // Body Weight
      summaryBody[0].vital_signs.push({
        date: observation?.resource?.effectiveDateTime?.split("T")[0],
        weight: `${value} ${observation?.resource?.valueQuantity?.unit}`,
      });
    }
  });

  // Update recent visits based on encounter
  summaryBody[0].recent_visits = [
    {
      date: encounter?.period?.start.split("T")?.[0] ?? "--",
      reason: encounter?.reasonCode?.[0]?.coding?.[0]?.display ?? "--",
      outcome: "Follow-up encounter",
    },
  ];

  return summaryBody;
}

const getSummary = async (medplum: MedplumClient, body: any) => {
  const summary = await aiHandler(medplum, { eventType: "summary", body });

  try {
    const parsedRes = summary?.body;

    if (parsedRes) {
      let parsedSumm = parseAndCategorize(parsedRes);
      const newparsed = addInputToJson(parsedSumm, summary.input);

      return newparsed;
    } else throw new Error("Invalid Summary - Please try again");
  } catch (err) {
    console.error("Unexpected error:", err);
  }
};
const aiHandler = async (medplum: MedplumClient, payload: any) => {
  try {
    const res = await medplum.executeBot(
      {
        system: "https://api-stage.healthconnect.systems/bots",
        value: "embedAi",
      },
      payload
    );
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
};
const initSummaryTags = {
  diseases: [] as string[],
  drugs: [] as string[],
  observations: [] as string[],
};
export const formatAiSummary = async (
  medplum: MedplumClient,
  body: any,
  setAiResponse: any,
  setCardioPrediction: any,
  setSummaryTags: any,
  setCardioRed?: any
) => {
  setCardioPrediction(undefined);
  setAiResponse("Generating...");
  setSummaryTags(initSummaryTags);
  const summaryDetails = await getSummary(medplum, body);

  if (!summaryDetails?.recommendation) {
    setAiResponse("Please try again...");
    showNotification({
      color: "red",
      message: "Something went wrong while generating AI summary",
      autoClose: true,
    });
    return null;
  }
  setSummaryTags({
    diseases: summaryDetails?.disease ?? [],
    drugs: summaryDetails?.drug ?? [],
    observations: summaryDetails?.observation ?? [],
  });
  setAiResponse(summaryDetails?.recommendation ?? "");
  return summaryDetails;
};
export const formatCardioSummary = async (
  medplum: MedplumClient,
  patientResource: any,
  observationResource: any,
  setAiResponse: any,
  setCardioPrediction: any,
  setSummaryTags: any,
  setCardioRed?: any
) => {
  setCardioPrediction(undefined);
  setAiResponse("Generating...");
  setSummaryTags(initSummaryTags);
  const prediction = await getCardioPrediction(
    medplum,
    observationResource,
    patientResource,
    
  );
  if (!prediction?.Result) {
    setAiResponse("Please try again...");
    showNotification({
      color: "red",
      message: "Something went wrong while generating Cardio Summary",
      autoClose: true,
    });
    return null;
  }
  let summary = "";
  if (prediction?.Result?.includes("Presence")) {
    const prob =
      prediction?.["Presence of Cardiovascular Disease Probability"] * 100;
    summary = `As per the data available, the patient has a <span style=\"color:#FF0000\">${Math.floor(
      prob
    )}%</span> probability of having <span style=\"color:#ff9000\">cardiovascular</span> disease.`;
  } else {
    const prob =
      100 - prediction?.["Absence of Cardiovascular Disease Probability"] * 100;
    summary = `As per the data available, the patient has a <span style=\"color:#008000\">${Math.floor(
      prob
    )}%</span> probability of having <span style=\"color:#ff9000\">cardiovascular</span> disease.`;
  }
  setCardioPrediction(prediction?.Result);
  if (setCardioRed) setCardioRed(prediction?.Result?.includes("Presence"));
  setAiResponse(summary);
  return prediction;
};
const getCardioPrediction = async (
  medplum: MedplumClient,
  patientResource: any,
  observationResource: any
) => {
  const summary = await aiHandler(medplum, {
    eventType: "prediction-cardio",
    'observationResource': observationResource,

    'patientResource':patientResource,
  });
  try {
    const parsedSumm = JSON.parse(summary.body);
    const newparsed = addInputToJson(parsedSumm, summary?.input);

    return newparsed;
  } catch (err) {
    console.error("Unexpected error:", err);
    return null;
  }
};

export const formatReadmissionPrediction = async (
  medplum: MedplumClient,
  patientResource: any,
  encounterResource:any,
  setAiResponse: any,
  setCardioPrediction: any,
  setSummaryTags: any,
  setCardioRed?: any
) => {
  
  setCardioPrediction(undefined);
  setAiResponse("Generating...");
  setSummaryTags(initSummaryTags);
  const prediction = await getReadmissionPrediction(medplum, patientResource,encounterResource);
  if (!prediction) {
    setAiResponse("Please try again ...");
    showNotification({
      color: "red",
      message: "Something went wrong while generating Length Of Stay",
      autoClose: true,
    });
    return null;
  }

  let summary = "";
  const prob = (prediction["Readmission Time probability"] * 100) as number;
  summary = `As per the data available, the patient has a <span style=\"color:#1c7ed6\">${prob.toFixed(
    1
  )}% </span> probability of getting readmitted in <span style=\"color:#1c7ed6\">${
    prediction["Readmission Time in days"]
  } days</span> .`;

  setCardioPrediction(`Prediction - ${prob.toFixed(1)}%`);
  if (setCardioRed) setCardioRed(prob > 0.8);
  setAiResponse(summary);
  return prediction;
};
const getReadmissionPrediction = async (
  medplum: MedplumClient,
  patientResource: any,
  encounterResource:any
) => {
  try {
    const prediction = await aiHandler(medplum, {
      eventType: "prediction-readmission",
      patientResource,encounterResource
    });

    if (prediction?.statusCode == 200 && prediction?.body) {
      const parsedBody = JSON.parse(prediction.body);
      return { ...parsedBody, input: prediction.input };
    } else {
      throw new Error("Invalid Prediction - Please try again");
    }
  } catch (err) {
    console.error("Unexpected error:", err);
    return null;
  }
};
export const formatLosPrediction = async (
  medplum: MedplumClient,
  patientResource:any,
  coverageResource:any,
  encounterResource:any,
  conditionResource:any,
  procedureResource:any,
  setAiResponse: any,
  setCardioPrediction: any,
  setSummaryTags: any,
  setCardioRed?: any
) => {
  setCardioPrediction(undefined);
  setAiResponse("Generating...");
  setSummaryTags(initSummaryTags);
  const prediction = await getLOSPrediction(medplum, patientResource,coverageResource,conditionResource,procedureResource,encounterResource);

  if (!prediction) {
    setAiResponse("Please try again...");
    showNotification({
      color: "red",
      message: "Something went wrong while generating Length Of Stay",
      autoClose: true,
    });
    return null;
  }
  let summary = "";
  const los = prediction?.body[
    "predicted Length of Stay(LOS) in days"
  ] as number;
  summary = `As per the data available, the patient has a predicted stay of <span style=\"color:#1c7ed6\">${los.toFixed(
    1
  )} days</span> .`;

  setCardioPrediction(`Prediction - ${los.toFixed(1)} days`);
  if (setCardioRed) setCardioRed(los > 5);
  setAiResponse(summary);
  return prediction;
};
const getLOSPrediction = async (
  medplum: MedplumClient,
  patientResource: any,
  coverageResource: any,
  encounterResource: any,
  conditionResource: any,
  procedureResource: any
) => {
  try {
   
    const summary = await aiHandler(medplum, {
      eventType: "prediction-los",
      patientResource: patientResource,
      coverageResource: coverageResource,
      encounterResource: encounterResource,
      conditionResource: conditionResource,
      procedureResource: procedureResource,
    });
    if (summary?.statusCode == 200 && summary?.body) {
      return {
        body: summary.body,
        input: summary.input,
      };
    } else throw new Error("Invalid Summary - Please try again");
  } catch (err) {
    console.error("Unexpected error:", err);
    return null;
  }
};
export const getSymptoms = async (
  medplum: MedplumClient,
  patientResource: any,
  conditionResource: any,
  symptoms: any,
  heightResource: any,
  weightResource: any,
  heartRateResource: any,
  bloodPressureResource: any
) => {
  const symptom = await aiHandler(medplum, {
    eventType: "prediction-disease",
    symptoms: symptoms,
    patientResource: patientResource,
    conditionResource: conditionResource,
    heightResource: heightResource,
    weightResource: weightResource,
    heartRateResource: heartRateResource,
    bloodPressureResource: bloodPressureResource,
  });
  try {
    const parsedSymtom = symptom.body;
    const newparsed = addInputToJson(parsedSymtom, symptom?.input);

    return newparsed;
  } catch (err) {
    console.error("Unexpected error:", err);
    return null;
  }
};
export function extractNoteTexts(data: any) {
  // Initialize an empty array to hold text values
  const noteTexts: any[] = [];

  // Loop through each object in the array
  data?.forEach((item: any) => {
    // Check if the object has a 'code' property and it contains 'text'
    if (item?.resource && item?.resource?.code && item?.resource?.code?.text) {
      // Append the 'text' from 'code' to the array
      noteTexts.push(item?.resource?.code?.text);
    }
  });

  // Join all text values with a comma and return the result
  return noteTexts.join(", ");
}

type AiName = "summary" | "los" | "cardio" | "symptom" | "reAdmission";

export const getAiNameDisplay = (
  name: AiName,
  toLowerCase: boolean = false
): string => {
  const mapping: Record<AiName, string> = {
    summary: "Medical Summary",
    los: "Length Of Stay Prediction",
    cardio: "Cardiovascular Risk Prediction",
    symptom: "Medical Symptoms",
    reAdmission: "Readmission Prediction",
  };

  const displayName = mapping[name] || "HealthConnect AI";

  return toLowerCase ? displayName.toLowerCase() : displayName;
};